/** @typedef {'search'|'results'|'details'|'passengers'|'extras'|'review'|'payment'|'confirmation'} Fe2Step */

/** @type {{ id: Fe2Step, label: string, inProgress: boolean }[]} */
export const FE2_PREVIEW_STEPS = [
  { id: "search", label: "חיפוש", inProgress: false },
  { id: "results", label: "תוצאות", inProgress: false },
  { id: "details", label: "טיסה", inProgress: false },
  { id: "passengers", label: "נוסעים", inProgress: false },
  { id: "extras", label: "תוספות", inProgress: false },
  { id: "review", label: "סיכום", inProgress: true },
  { id: "payment", label: "תשלום", inProgress: true },
  { id: "confirmation", label: "אישור", inProgress: true },
];

export const FE2_BOOKING_PROGRESS = [
  "details",
  "passengers",
  "extras",
  "review",
  "payment",
  "confirmation",
];

export const MOCK_SEARCH = {
  origin: "TLV",
  originCity: "תל אביב",
  destination: "LCA",
  destinationCity: "לרנקה",
  tripType: "roundtrip",
  tripTypeLabel: "הלוך-חזור",
  departDate: "2026-10-08",
  departDateLabel: "08/10/2026",
  returnDate: "2026-10-11",
  returnDateLabel: "11/10/2026",
  passengers: { adults: 2, children: 0, infants: 0 },
  passengersLabel: "2 מבוגרים",
  cabin: "economy",
  cabinLabel: "תיירות",
};

/** Flexible date strip — mock prices only for Design Preview. */
export const MOCK_DATE_STRIP = [
  { id: "d-05", weekday: "ראשון", dateLabel: "05/10", priceLabel: "החל מ-$238", selected: false, cheapest: false },
  { id: "d-06", weekday: "שני", dateLabel: "06/10", priceLabel: "החל מ-$225", selected: false, cheapest: false },
  { id: "d-07", weekday: "שלישי", dateLabel: "07/10", priceLabel: "החל מ-$205", selected: false, cheapest: false },
  { id: "d-08", weekday: "רביעי", dateLabel: "08/10", priceLabel: "$190", selected: true, cheapest: true },
  { id: "d-09", weekday: "חמישי", dateLabel: "09/10", priceLabel: "$212", selected: false, cheapest: false },
  { id: "d-10", weekday: "שישי", dateLabel: "10/10", priceLabel: "החל מ-$218", selected: false, cheapest: false },
  { id: "d-11", weekday: "שבת", dateLabel: "11/10", priceLabel: "החל מ-$229", selected: false, cheapest: false },
];

export const MOCK_FILTERS = {
  stops: [
    { id: "direct", label: "ישיר", count: 42, checked: true },
    { id: "1stop", label: "עצירה אחת", count: 21, checked: false },
    { id: "2plus", label: "2+ עצירות", count: 8, checked: false },
  ],
  airlines: [
    { id: "IZ", label: "Arkia", heLabel: "ארקיע", count: 18, checked: true },
    { id: "6H", label: "Israir", heLabel: "ישראייר", count: 15, checked: false },
    { id: "LY", label: "EL AL", heLabel: "אל על", count: 12, checked: false },
    { id: "A3", label: "Aegean", heLabel: "איג'יאן", count: 9, checked: false },
    { id: "FR", label: "Ryanair", heLabel: "ריינאייר", count: 7, checked: false },
  ],
  baggage: [
    { id: "trolley", label: "טרולי", count: 28, checked: false },
    { id: "checked", label: "מזוודה", count: 19, checked: false },
    { id: "none", label: "ללא מזוודה", count: 6, checked: false },
    { id: "paid", label: "כבודה בתשלום", count: 24, checked: false },
  ],
  outboundTime: {
    selected: null,
    options: [
      { id: "morning", label: "בוקר", rangeLabel: "05–12", checked: false },
      { id: "afternoon", label: "צהריים", rangeLabel: "12–17", checked: false },
      { id: "evening", label: "ערב", rangeLabel: "17–21", checked: false },
      { id: "night", label: "לילה", rangeLabel: "21–05", checked: false },
    ],
  },
  returnTime: {
    selected: null,
    options: [
      { id: "morning", label: "בוקר", rangeLabel: "05–12", checked: false },
      { id: "afternoon", label: "צהריים", rangeLabel: "12–17", checked: false },
      { id: "evening", label: "ערב", rangeLabel: "17–21", checked: false },
      { id: "night", label: "לילה", rangeLabel: "21–05", checked: false },
    ],
  },
  resultCount: 71,
};

/** Dev-only Details preview scenarios. */
export const DETAILS_PREVIEW_STATES = [
  { id: "direct-paid", label: "Direct · baggage paid", offerId: "mock-arkia-cheapest" },
  { id: "direct-included", label: "Direct · trolley included", offerId: "mock-israir-trolley" },
  { id: "one-stop", label: "1 stop · baggage included", offerId: "mock-aegean-1stop" },
  { id: "mixed", label: "Mixed carrier · connection", offerId: "mock-mixed-connection" },
  { id: "reprice", label: "Price change / reprice", offerId: "mock-arkia-cheapest", reprice: true },
];

export const MOCK_REPRICE = {
  previousPerPassengerLabel: "$190",
  newPerPassengerLabel: "$203",
  previousTotalLabel: "₪1,178",
  newTotalLabel: "₪1,259",
  message: "המחיר התעדכן מאז בחירת הטיסה",
};

/**
 * Design Preview ViewModels — Results + Details fields.
 * Extra Details fields are ignored by Results cards.
 */
export const MOCK_OFFERS = [
  {
    id: "mock-arkia-cheapest",
    airline: { code: "IZ", name: "Arkia", nameHe: "ארקיע", logoText: "IZ" },
    fareBadges: ["CHARTER"],
    flags: { cheapest: true, direct: true },
    outbound: {
      title: "טיסת הלוך",
      weekdayLabel: "יום חמישי, 08/10/2026",
      depTime: "16:30",
      depIata: "TLV",
      depCity: "תל אביב",
      arrTime: "17:30",
      arrIata: "LCA",
      arrCity: "לרנקה",
      duration: "1:00",
      stops: 0,
      stopsLabel: "ישיר",
      stopsDetailLabel: "טיסה ישירה",
      date: "08/10/2026",
      flightNumber: "IZ1163",
      operatedBy: "Arkia",
      cabin: "Economy",
      aircraft: "Airbus A321",
    },
    inbound: {
      title: "טיסת חזור",
      weekdayLabel: "יום ראשון, 11/10/2026",
      depTime: "09:50",
      depIata: "LCA",
      depCity: "לרנקה",
      arrTime: "10:50",
      arrIata: "TLV",
      arrCity: "תל אביב",
      duration: "1:00",
      stops: 0,
      stopsLabel: "ישיר",
      date: "11/10/2026",
      flightNumber: "IZ164",
      operatedBy: "Arkia",
      cabin: "Economy",
      aircraft: "Airbus A321",
    },
    baggage: {
      chips: [
        { tone: "paid", label: "מזוודה בתשלום" },
        { tone: "info", label: "תיירות" },
      ],
      detailChips: [
        { tone: "paid", label: "מזוודה בתשלום" },
        { tone: "info", label: "מחלקת Economy" },
        { tone: "info", label: "CHARTER" },
      ],
      summaryLines: ["מזוודה בתשלום", "טרולי לא צוין"],
    },
    fare: { cabin: "Economy", family: "CHARTER" },
    price: {
      perPassengerLabel: "$190",
      perPassengerValue: 190,
      quantity: 2,
      totalLabel: "₪1,178",
      totalNote: "סה״כ ל־2 נוסעים",
      lineLabel: "$190 × 2",
    },
    conditions: {
      changes: "בתשלום",
      cancellation: "לא ניתן",
      refund: "לא ניתן להחזר",
      ticketingDeadline: "24 שעות לאחר הזמנה",
      fullTerms:
        "שינוי כרטיס כפוף לזמינות ולעמלת ספק. ביטול אינו מזכה בהחזר. כרטוס נדרש תוך 24 שעות מאישור ההזמנה.",
    },
    alerts: [],
    commission: { label: "עמלת סוכן (mock)", valueLabel: "₪94" },
    bookingSummary: {
      flights: {
        title: "מחיר טיסות",
        lineLabel: "2 נוסעים × $190",
        amountLabel: "$380.00",
      },
      baggage: {
        title: "כבודה",
        items: [
          {
            label: "טרולי — לא כלול",
            secondary: "ניתן להוסיף בתשלום",
            valueLabel: "—",
            tone: "muted",
            ltr: false,
          },
          {
            label: "מזוודה — לא כלולה",
            secondary: "ניתן להוסיף בתשלום",
            valueLabel: "—",
            tone: "muted",
            ltr: false,
          },
        ],
        extraLine: { label: "תוספת כבודה", amountLabel: "$0.00" },
      },
      extras: {
        title: "תוספות",
        emptyLabel: "תוספות",
        emptyAmountLabel: "$0.00",
        items: [],
      },
      fare: { title: "Fare", family: "CHARTER", cabin: "Economy" },
      sourceTotal: {
        title: "סה״כ במטבע העסקה",
        amountLabel: "$380.00",
        currency: "USD",
      },
      exchangeRate: {
        title: "שער דולר להזמנה",
        rateLabel: "$1 = ₪3.0987",
        note: "שער ההמרה שנשמר עבור ההזמנה",
        mock: true,
      },
      totals: {
        calculatedLabel: "סה״כ מחושב",
        calculatedTotalLabel: "₪1,177.51",
        roundingLabel: "עיגול",
        roundingAdjustmentLabel: "+₪0.49",
        payableLabel: "לתשלום",
        payableTotalLabel: "₪1,178",
      },
      commission: { label: "עמלת סוכן (mock)", valueLabel: "₪94" },
    },
  },
  {
    id: "mock-israir-trolley",
    airline: { code: "6H", name: "Israir", nameHe: "ישראייר", logoText: "6H" },
    fareBadges: ["CHARTER"],
    flags: { cheapest: false, direct: true },
    outbound: {
      title: "טיסת הלוך",
      weekdayLabel: "יום חמישי, 08/10/2026",
      depTime: "11:00",
      depIata: "TLV",
      depCity: "תל אביב",
      arrTime: "12:05",
      arrIata: "LCA",
      arrCity: "לרנקה",
      duration: "1:05",
      stops: 0,
      stopsLabel: "ישיר",
      stopsDetailLabel: "טיסה ישירה",
      date: "08/10/2026",
      flightNumber: "6H581",
      operatedBy: "Israir",
      cabin: "Economy",
      aircraft: "Airbus A320",
    },
    inbound: {
      title: "טיסת חזור",
      weekdayLabel: "יום ראשון, 11/10/2026",
      depTime: "18:30",
      depIata: "LCA",
      depCity: "לרנקה",
      arrTime: "19:35",
      arrIata: "TLV",
      arrCity: "תל אביב",
      duration: "1:05",
      stops: 0,
      stopsLabel: "ישיר",
      date: "11/10/2026",
      flightNumber: "6H582",
      operatedBy: "Israir",
      cabin: "Economy",
      aircraft: "Airbus A320",
    },
    baggage: {
      chips: [
        { tone: "positive", label: "✓ טרולי 8 ק״ג" },
        { tone: "info", label: "תיירות" },
      ],
      detailChips: [
        { tone: "positive", label: "✓ טרולי 8 ק״ג" },
        { tone: "info", label: "מחלקת Economy" },
        { tone: "info", label: "CHARTER" },
      ],
      summaryLines: ["טרולי 8 ק״ג כלול"],
    },
    fare: { cabin: "Economy", family: "CHARTER" },
    price: {
      perPassengerLabel: "$202",
      perPassengerValue: 202,
      quantity: 2,
      totalLabel: "₪1,252",
      totalNote: "סה״כ ל־2 נוסעים",
      lineLabel: "$202 × 2",
    },
    conditions: {
      changes: "בתשלום",
      cancellation: "מוגבל",
      refund: "לא ניתן להחזר",
      ticketingDeadline: "48 שעות",
      fullTerms: "טרולי כלול בכרטיס. שינוי כפוף לעמלת ספק.",
    },
    alerts: [],
    commission: { label: "עמלת סוכן (mock)", valueLabel: "₪100" },
    bookingSummary: {
      flights: {
        title: "מחיר טיסות",
        lineLabel: "2 נוסעים × $202",
        amountLabel: "$404.00",
      },
      baggage: {
        title: "כבודה",
        items: [
          {
            label: "טרולי 8 ק״ג",
            valueLabel: "כלול",
            tone: "positive",
            ltr: false,
          },
          {
            label: "מזוודה — לא כלולה",
            secondary: "ניתן להוסיף בתשלום",
            valueLabel: "—",
            tone: "muted",
            ltr: false,
          },
        ],
        extraLine: { label: "תוספת כבודה", amountLabel: "$0.00" },
      },
      extras: {
        title: "תוספות",
        emptyLabel: "תוספות",
        emptyAmountLabel: "$0.00",
        items: [],
      },
      fare: { title: "Fare", family: "CHARTER", cabin: "Economy" },
      sourceTotal: {
        title: "סה״כ במטבע העסקה",
        amountLabel: "$404.00",
        currency: "USD",
      },
      exchangeRate: {
        title: "שער דולר להזמנה",
        rateLabel: "$1 = ₪3.0987",
        note: "שער ההמרה שנשמר עבור ההזמנה",
        mock: true,
      },
      totals: {
        calculatedLabel: "סה״כ מחושב",
        calculatedTotalLabel: "₪1,251.87",
        roundingLabel: "עיגול",
        roundingAdjustmentLabel: "+₪0.13",
        payableLabel: "לתשלום",
        payableTotalLabel: "₪1,252",
      },
      commission: { label: "עמלת סוכן (mock)", valueLabel: "₪100" },
    },
  },
  {
    id: "mock-aegean-1stop",
    airline: { code: "A3", name: "Aegean", nameHe: "איג'יאן", logoText: "A3" },
    fareBadges: ["REGULAR"],
    flags: { cheapest: false, direct: false },
    outbound: {
      title: "טיסת הלוך",
      weekdayLabel: "יום חמישי, 08/10/2026",
      depTime: "06:40",
      depIata: "TLV",
      depCity: "תל אביב",
      arrTime: "11:30",
      arrIata: "LCA",
      arrCity: "לרנקה",
      duration: "4:50",
      stops: 1,
      stopsLabel: "עצירה 1",
      connection: "ATH · 1:20",
      date: "08/10/2026",
      flightNumber: "A3912",
      operatedBy: "Aegean",
      cabin: "Economy",
      aircraft: "Airbus A320",
      segments: [
        {
          depTime: "06:40",
          depIata: "TLV",
          depCity: "תל אביב",
          arrTime: "08:55",
          arrIata: "ATH",
          arrCity: "אתונה",
          flightNumber: "A3912",
          duration: "2:15",
        },
        {
          type: "connection",
          airport: "ATH",
          airportName: "אתונה",
          duration: "1:20",
          terminal: "Terminal B",
        },
        {
          depTime: "10:15",
          depIata: "ATH",
          depCity: "אתונה",
          arrTime: "11:30",
          arrIata: "LCA",
          arrCity: "לרנקה",
          flightNumber: "A3904",
          duration: "1:15",
        },
      ],
    },
    inbound: {
      title: "טיסת חזור",
      weekdayLabel: "יום ראשון, 11/10/2026",
      depTime: "14:15",
      depIata: "LCA",
      depCity: "לרנקה",
      arrTime: "19:05",
      arrIata: "TLV",
      arrCity: "תל אביב",
      duration: "4:50",
      stops: 1,
      stopsLabel: "עצירה 1",
      connection: "ATH · 1:35",
      date: "11/10/2026",
      flightNumber: "A3913",
      operatedBy: "Aegean",
      cabin: "Economy",
      aircraft: "Airbus A320",
      segments: [
        {
          depTime: "14:15",
          depIata: "LCA",
          depCity: "לרנקה",
          arrTime: "15:30",
          arrIata: "ATH",
          arrCity: "אתונה",
          flightNumber: "A3905",
          duration: "1:15",
        },
        {
          type: "connection",
          airport: "ATH",
          airportName: "אתונה",
          duration: "1:35",
          terminal: "Terminal B",
        },
        {
          depTime: "17:05",
          depIata: "ATH",
          depCity: "אתונה",
          arrTime: "19:05",
          arrIata: "TLV",
          arrCity: "תל אביב",
          flightNumber: "A3913",
          duration: "2:00",
        },
      ],
    },
    baggage: {
      chips: [
        { tone: "positive", label: "✓ טרולי 8 ק״ג" },
        { tone: "positive", label: "✓ מזוודה 23 ק״ג" },
        { tone: "info", label: "תיירות" },
      ],
      detailChips: [
        { tone: "positive", label: "✓ טרולי 8 ק״ג" },
        { tone: "positive", label: "✓ מזוודה 23 ק״ג" },
        { tone: "info", label: "מחלקת Economy" },
        { tone: "info", label: "REGULAR" },
      ],
      summaryLines: ["טרולי 8 ק״ג", "מזוודה 23 ק״ג"],
    },
    fare: { cabin: "Economy", family: "REGULAR" },
    price: {
      perPassengerLabel: "$265",
      perPassengerValue: 265,
      quantity: 2,
      totalLabel: "₪1,643",
      totalNote: "סה״כ ל־2 נוסעים",
      lineLabel: "$265 × 2",
    },
    conditions: {
      changes: "בתשלום",
      cancellation: "בתשלום",
      refund: "לפי תנאי fare",
      ticketingDeadline: "מיידי",
      fullTerms: "כבודה כלולה לפי fare. עצירה באתונה באותו טרמינל.",
    },
    alerts: [
      {
        tone: "amber",
        title: "עצירה באתונה",
        body: "זמן חיבור 1:20 · Terminal B · אין שינוי שדה תעופה",
      },
    ],
    commission: { label: "עמלת סוכן (mock)", valueLabel: "₪132" },
    bookingSummary: {
      flights: {
        title: "מחיר טיסות",
        lineLabel: "2 נוסעים × $265",
        amountLabel: "$530.00",
      },
      baggage: {
        title: "כבודה",
        items: [
          {
            label: "טרולי 8 ק״ג",
            valueLabel: "כלול",
            tone: "positive",
            ltr: false,
          },
          {
            label: "מזוודה 23 ק״ג",
            valueLabel: "כלול",
            tone: "positive",
            ltr: false,
          },
        ],
        extraLine: { label: "תוספת כבודה", amountLabel: "$0.00" },
      },
      extras: {
        title: "תוספות",
        emptyLabel: "תוספות",
        emptyAmountLabel: "$0.00",
        items: [],
      },
      fare: { title: "Fare", family: "REGULAR", cabin: "Economy" },
      sourceTotal: {
        title: "סה״כ במטבע העסקה",
        amountLabel: "$530.00",
        currency: "USD",
      },
      exchangeRate: {
        title: "שער דולר להזמנה",
        rateLabel: "$1 = ₪3.0987",
        note: "שער ההמרה שנשמר עבור ההזמנה",
        mock: true,
      },
      totals: {
        calculatedLabel: "סה״כ מחושב",
        calculatedTotalLabel: "₪1,642.31",
        roundingLabel: "עיגול",
        roundingAdjustmentLabel: "+₪0.69",
        payableLabel: "לתשלום",
        payableTotalLabel: "₪1,643",
      },
      commission: { label: "עמלת סוכן (mock)", valueLabel: "₪132" },
    },
  },
  {
    id: "mock-elal-direct",
    airline: { code: "LY", name: "EL AL", nameHe: "אל על", logoText: "LY" },
    fareBadges: ["REGULAR"],
    flags: { cheapest: false, direct: true },
    outbound: {
      title: "טיסת הלוך",
      weekdayLabel: "יום חמישי, 08/10/2026",
      depTime: "08:15",
      depIata: "TLV",
      depCity: "תל אביב",
      arrTime: "09:20",
      arrIata: "LCA",
      arrCity: "לרנקה",
      duration: "1:05",
      stops: 0,
      stopsLabel: "ישיר",
      date: "08/10/2026",
      flightNumber: "LY5121",
      operatedBy: "EL AL",
      cabin: "Economy",
    },
    inbound: {
      title: "טיסת חזור",
      weekdayLabel: "יום ראשון, 11/10/2026",
      depTime: "16:40",
      depIata: "LCA",
      depCity: "לרנקה",
      arrTime: "17:45",
      arrIata: "TLV",
      arrCity: "תל אביב",
      duration: "1:05",
      stops: 0,
      stopsLabel: "ישיר",
      date: "11/10/2026",
      flightNumber: "LY5122",
      operatedBy: "EL AL",
      cabin: "Economy",
    },
    baggage: {
      chips: [
        { tone: "positive", label: "✓ טרולי 8 ק״ג" },
        { tone: "paid", label: "מזוודה בתשלום" },
        { tone: "info", label: "תיירות" },
      ],
      detailChips: [
        { tone: "positive", label: "✓ טרולי 8 ק״ג" },
        { tone: "paid", label: "מזוודה בתשלום" },
        { tone: "info", label: "מחלקת Economy" },
      ],
      summaryLines: ["טרולי 8 ק״ג", "מזוודה בתשלום"],
    },
    fare: { cabin: "Economy", family: "REGULAR" },
    price: {
      perPassengerLabel: "$308",
      perPassengerValue: 308,
      quantity: 2,
      totalLabel: "₪1,910",
      totalNote: "סה״כ ל־2 נוסעים",
      lineLabel: "$308 × 2",
    },
    conditions: {
      changes: "בתשלום",
      cancellation: "בתשלום",
      refund: "לפי fare",
      ticketingDeadline: "12 שעות",
      fullTerms: "טרולי כלול. מזוודה זמינה ברכישה נפרדת.",
    },
    alerts: [],
    commission: { label: "עמלת סוכן (mock)", valueLabel: "₪154" },
    bookingSummary: {
      flights: {
        title: "מחיר טיסות",
        lineLabel: "2 נוסעים × $308",
        amountLabel: "$616.00",
      },
      baggage: {
        title: "כבודה",
        items: [
          {
            label: "טרולי 8 ק״ג",
            valueLabel: "כלול",
            tone: "positive",
            ltr: false,
          },
          {
            label: "מזוודה 20 ק״ג",
            valueLabel: "+$35",
            tone: "paid",
            ltr: true,
          },
        ],
      },
      extras: {
        title: "תוספות",
        emptyLabel: "תוספות",
        emptyAmountLabel: "$0.00",
        items: [],
      },
      fare: { title: "Fare", family: "REGULAR", cabin: "Economy" },
      sourceTotal: {
        title: "סה״כ במטבע העסקה",
        amountLabel: "$651.00",
        currency: "USD",
      },
      exchangeRate: {
        title: "שער דולר להזמנה",
        rateLabel: "$1 = ₪3.0987",
        note: "שער ההמרה שנשמר עבור ההזמנה",
        mock: true,
      },
      totals: {
        calculatedLabel: "סה״כ מחושב",
        calculatedTotalLabel: "₪2,017.25",
        roundingLabel: "עיגול",
        roundingAdjustmentLabel: "+₪0.75",
        payableLabel: "לתשלום",
        payableTotalLabel: "₪2,018",
      },
      commission: { label: "עמלת סוכן (mock)", valueLabel: "₪154" },
    },
  },
  {
    id: "mock-mixed-connection",
    airline: { code: "LY", name: "EL AL + Aegean", nameHe: "אל על + איג'יאן", logoText: "LY" },
    secondaryAirline: { code: "A3", name: "Aegean", nameHe: "איג'יאן", logoText: "A3" },
    fareBadges: ["REGULAR"],
    flags: { cheapest: false, direct: false },
    outbound: {
      title: "טיסת הלוך",
      weekdayLabel: "יום חמישי, 08/10/2026",
      depTime: "05:50",
      depIata: "TLV",
      depCity: "תל אביב",
      arrTime: "11:15",
      arrIata: "LCA",
      arrCity: "לרנקה",
      duration: "5:25",
      stops: 1,
      stopsLabel: "עצירה 1",
      connection: "ATH · 1:20",
      date: "08/10/2026",
      flightNumber: "LY541",
      operatedBy: "EL AL",
      cabin: "Economy",
      segments: [
        {
          depTime: "05:50",
          depIata: "TLV",
          depCity: "תל אביב",
          arrTime: "08:05",
          arrIata: "ATH",
          arrCity: "אתונה",
          flightNumber: "LY541",
          duration: "2:15",
          operatedBy: "EL AL",
        },
        {
          type: "connection",
          airport: "ATH",
          airportName: "אתונה",
          duration: "1:20",
          terminal: "Terminal A → B",
          selfTransfer: true,
          airportChange: true,
        },
        {
          depTime: "09:25",
          depIata: "ATH",
          depCity: "אתונה",
          arrTime: "11:15",
          arrIata: "LCA",
          arrCity: "לרנקה",
          flightNumber: "A3904",
          duration: "1:50",
          operatedBy: "Aegean",
        },
      ],
    },
    inbound: {
      title: "טיסת חזור",
      weekdayLabel: "יום ראשון, 11/10/2026",
      depTime: "13:05",
      depIata: "LCA",
      depCity: "לרנקה",
      arrTime: "18:30",
      arrIata: "TLV",
      arrCity: "תל אביב",
      duration: "5:25",
      stops: 1,
      stopsLabel: "עצירה 1",
      connection: "ATH · 1:35",
      date: "11/10/2026",
      flightNumber: "A3912",
      operatedBy: "Aegean",
      cabin: "Economy",
      segments: [
        {
          depTime: "13:05",
          depIata: "LCA",
          depCity: "לרנקה",
          arrTime: "14:20",
          arrIata: "ATH",
          arrCity: "אתונה",
          flightNumber: "A3912",
          duration: "1:15",
          operatedBy: "Aegean",
        },
        {
          type: "connection",
          airport: "ATH",
          airportName: "אתונה",
          duration: "1:35",
          terminal: "Terminal B → A",
          selfTransfer: true,
          airportChange: true,
        },
        {
          depTime: "15:55",
          depIata: "ATH",
          depCity: "אתונה",
          arrTime: "18:30",
          arrIata: "TLV",
          arrCity: "תל אביב",
          flightNumber: "LY542",
          duration: "2:35",
          operatedBy: "EL AL",
        },
      ],
    },
    baggage: {
      chips: [
        { tone: "paid", label: "כבודה בתשלום" },
        { tone: "info", label: "תיירות" },
      ],
      detailChips: [
        { tone: "paid", label: "כבודה בתשלום" },
        { tone: "info", label: "מחלקת Economy" },
        { tone: "info", label: "REGULAR" },
      ],
      summaryLines: ["כבודה בתשלום"],
    },
    fare: { cabin: "Economy", family: "REGULAR" },
    price: {
      perPassengerLabel: "$279",
      perPassengerValue: 279,
      quantity: 2,
      totalLabel: "₪1,730",
      totalNote: "סה״כ ל־2 נוסעים",
      lineLabel: "$279 × 2",
    },
    conditions: {
      changes: "בתשלום",
      cancellation: "חלקי",
      refund: "לפי fare",
      ticketingDeadline: "12 שעות",
      fullTerms: "טיסה משולבת. self-transfer באתונה — אין אחריות ספק על החמצת חיבור.",
    },
    alerts: [
      {
        tone: "amber",
        title: "Self-transfer באתונה",
        body: "יש לאסוף כבודה ולהירשם מחדש לטיסה השנייה",
      },
      {
        tone: "amber",
        title: "שינוי טרמינל",
        body: "Terminal A → B · יש להקצות זמן מעבר נוסף",
      },
      {
        tone: "info",
        title: "מופעל על ידי חברות שונות",
        body: "הלוך: EL AL · חזור: Aegean / EL AL",
      },
    ],
    commission: { label: "עמלת סוכן (mock)", valueLabel: "₪140" },
    bookingSummary: {
      flights: {
        title: "מחיר טיסות",
        lineLabel: "2 נוסעים × $279",
        amountLabel: "$558.00",
      },
      baggage: {
        title: "כבודה",
        items: [
          {
            label: "טרולי — לא כלול",
            secondary: "ניתן להוסיף בתשלום",
            valueLabel: "—",
            tone: "muted",
            ltr: false,
          },
          {
            label: "מזוודה 20 ק״ג",
            valueLabel: "+$35",
            tone: "paid",
            ltr: true,
          },
        ],
      },
      extras: {
        title: "תוספות",
        emptyLabel: "תוספות",
        emptyAmountLabel: "$0.00",
        items: [{ label: "מושב", amountLabel: "$12" }],
      },
      fare: { title: "Fare", family: "REGULAR", cabin: "Economy" },
      sourceTotal: {
        title: "סה״כ במטבע העסקה",
        amountLabel: "$605.00",
        currency: "USD",
      },
      exchangeRate: {
        title: "שער דולר להזמנה",
        rateLabel: "$1 = ₪3.0987",
        note: "שער ההמרה שנשמר עבור ההזמנה",
        mock: true,
      },
      totals: {
        calculatedLabel: "סה״כ מחושב",
        calculatedTotalLabel: "₪1,874.71",
        roundingLabel: "עיגול",
        roundingAdjustmentLabel: "+₪0.29",
        payableLabel: "לתשלום",
        payableTotalLabel: "₪1,875",
      },
      commission: { label: "עמלת סוכן (mock)", valueLabel: "₪140" },
    },
    fareFamilies: [
      {
        id: "basic",
        name: "Economy Basic",
        priceLabel: "$279",
        trolley: "טרולי בלבד",
        checked: "מזוודה בתשלום",
        changes: "לא ניתן",
        refund: "לא ניתן",
      },
      {
        id: "standard",
        name: "Economy Standard",
        priceLabel: "$319",
        trolley: "טרולי 8 ק״ג",
        checked: "מזוודה 23 ק״ג",
        changes: "בתשלום",
        refund: "לא ניתן",
      },
      {
        id: "flex",
        name: "Economy Flex",
        priceLabel: "$389",
        trolley: "טרולי 8 ק״ג",
        checked: "מזוודה 23 ק״ג",
        changes: "כלול",
        refund: "חלקי",
      },
    ],
  },
];

export function getMockOfferById(id) {
  return MOCK_OFFERS.find((o) => o.id === id) || MOCK_OFFERS[0];
}

/**
 * Passengers Design Preview ViewModel — presentation only.
 * No validation / API / SSOT writes here.
 */
export const MOCK_PASSENGERS_FORM = {
  introTitle: "פרטי נוסעים",
  namesHint: "יש להזין את השמות באנגלית בדיוק כפי שמופיעים בדרכון.",
  passportDeferredNote: "פרטי הדרכון יידרשו בהמשך",
  genderOptions: [
    { value: "", label: "בחרו" },
    { value: "M", label: "זכר" },
    { value: "F", label: "נקבה" },
    { value: "X", label: "אחר" },
  ],
  nationalityOptions: [
    { value: "IL", label: "ישראל" },
    { value: "US", label: "ארצות הברית" },
    { value: "CY", label: "קפריסין" },
    { value: "GR", label: "יוון" },
    { value: "GB", label: "בריטניה" },
  ],
  documentTypeOptions: [
    { value: "passport", label: "דרכון" },
    { value: "id", label: "תעודת זהות" },
  ],
  statusLabels: {
    complete: "הושלם",
    incomplete: "חסר מידע",
    untouched: "לא הושלם",
  },
  fields: {
    firstName: { label: "שם פרטי", placeholder: "Given name" },
    middleName: { label: "שם אמצעי", placeholder: "Middle name", optional: true },
    lastName: { label: "שם משפחה", placeholder: "Family name" },
    gender: { label: "מין" },
    dateOfBirth: { label: "תאריך לידה" },
    nationality: { label: "אזרחות" },
    documentType: { label: "סוג מסמך" },
    passportNumber: { label: "מספר דרכון", placeholder: "Passport number" },
    issuingCountry: { label: "מדינת הנפקה" },
    passportIssueDate: { label: "תאריך הנפקה", optional: true },
    passportExpiry: { label: "תאריך תפוגה" },
  },
  contact: {
    title: "פרטי איש קשר להזמנה",
    note: "פרטי הקשר ישמשו לעדכונים הקשורים להזמנה.",
    name: { label: "שם", placeholder: "Contact name" },
    phone: { label: "טלפון", placeholder: "050-1234567" },
    email: { label: "Email", placeholder: "name@example.com" },
    values: {
      name: "Sara Levi",
      phone: "050-9876543",
      email: "sara.levi@example.com",
    },
  },
  passengers: [
    {
      id: "pax-1",
      indexLabel: "נוסע 1",
      typeLabel: "מבוגר",
      status: "complete",
      collapsed: true,
      values: {
        firstName: "David",
        middleName: "",
        lastName: "Cohen",
        gender: "M",
        dateOfBirth: "12/04/1988",
        nationality: "IL",
        documentType: "passport",
        passportNumber: "338877665",
        issuingCountry: "IL",
        passportIssueDate: "01/06/2021",
        passportExpiry: "01/06/2031",
      },
      errors: {},
    },
    {
      id: "pax-2",
      indexLabel: "נוסע 2",
      typeLabel: "מבוגר",
      status: "incomplete",
      collapsed: false,
      values: {
        firstName: "Noa",
        middleName: "",
        lastName: "",
        gender: "F",
        dateOfBirth: "03/09/1992",
        nationality: "IL",
        documentType: "passport",
        passportNumber: "112233445",
        issuingCountry: "IL",
        passportIssueDate: "10/02/2018",
        passportExpiry: "15/01/2024",
      },
      errors: {
        lastName: "שדה חובה",
        passportExpiry: "תוקף הדרכון אינו מספיק לנסיעה",
      },
    },
  ],
};

/** Passport form shown only when provider requires docs at this step. */
export function passengersRequirePassport(offer) {
  const family = String(offer?.fare?.family || offer?.bookingSummary?.fare?.family || "");
  return !/charter/i.test(family);
}
